import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  BadgeCheck,
  Bot,
  CircleCheck,
  ClipboardX,
  Hourglass,
  Loader2,
  MailWarning,
  RotateCcw,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { StatusBadge } from '@/components/quotes/status-badge'
import { PageHeader } from '@/components/shell/page'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@/lib/api'
import { ukDate } from '@/lib/quotes'
import { reportError } from '@/lib/settings'
import { useTitle } from '@/lib/use-title'

export const Route = createFileRoute('/_app/approvals')({ component: ToCheckPage })

import { attentionQuery, type QuoteItem } from '@/lib/attention'

const daysLeft = (d: string | null) =>
  d ? Math.ceil((new Date(`${d}T23:59:59`).getTime() - Date.now()) / 86_400_000) : null

function ToCheckPage() {
  useTitle('To check')
  const q = useQuery(attentionQuery)
  const qc = useQueryClient()
  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/mail/outbox/${id}/retry`),
    onSuccess: () => {
      toast.success('Trying that email again')
      void qc.invalidateQueries({ queryKey: ['attention'] })
    },
    onError: (e) => reportError(e),
  })
  const d = q.data

  return (
    <>
      <PageHeader
        title="To check"
        description="Everything waiting on a person. Clients approve quotes by signing them, so this is mostly Kira's drafts, proposals about to run out, and anything that went wrong."
      />
      {q.isPending ? (
        <div className="grid gap-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      ) : d && d.total === 0 ? (
        <section className="flex flex-col items-center gap-3 rounded-xl border border-dashed bg-card px-6 py-12 text-center">
          <span className="flex size-12 items-center justify-center rounded-full bg-success/15 text-success">
            <CircleCheck className="size-6" aria-hidden />
          </span>
          <h2 className="text-lg font-semibold">All clear</h2>
          <p className="max-w-[48ch] text-[15px] text-muted-foreground">
            Nothing is waiting on you. Drafts from Kira, proposals about to expire and failed emails
            will appear here.
          </p>
        </section>
      ) : d ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {d.agentDrafts.length > 0 && (
            <Group
              icon={<Bot />}
              title="Drafts prepared by Kira"
              hint="Check the prices and wording, then sign and send them yourself."
            >
              {d.agentDrafts.map((x) => (
                <QuoteLine key={x.id} q={x} meta={`Started ${ukDate(x.updatedAt, true)}`} />
              ))}
            </Group>
          )}
          {d.awaitingApproval.length > 0 && (
            <Group
              icon={<BadgeCheck />}
              title="Waiting for approval"
              hint={
                d.approvalMode === 'never'
                  ? 'Approval is switched off, so these can be signed and sent straight away.'
                  : 'Open each quote to approve it or send it back.'
              }
            >
              {d.awaitingApproval.map((x) => (
                <QuoteLine key={x.id} q={x} meta={`Prepared by ${x.ownerName}`} />
              ))}
            </Group>
          )}
          {d.expiringSoon.length > 0 && (
            <Group
              icon={<Hourglass />}
              title="Proposals running out this week"
              hint="A quick follow-up often gets these signed. Open the quote and use Send a follow-up."
            >
              {d.expiringSoon.map((x) => {
                const left = daysLeft(x.validUntil)
                return (
                  <QuoteLine
                    key={x.id}
                    q={x}
                    meta={
                      <span className="font-medium text-warning">
                        {left === 0
                          ? 'Expires today'
                          : `Expires in ${left} day${left === 1 ? '' : 's'}`}
                        {x.lastViewedAt ? ', opened' : ', not opened yet'}
                      </span>
                    }
                  />
                )
              })}
            </Group>
          )}
          {d.handoffProblems.length > 0 && (
            <Group
              icon={<ClipboardX />}
              title="Admin set-up problems"
              hint="QuickBooks or SmartTask tasks that could not be finished."
            >
              {d.handoffProblems.map((h) => (
                <li key={h.id}>
                  <Link
                    to="/quotes/$id"
                    params={{ id: h.quoteId }}
                    className="-mx-2 block rounded-md px-2 py-2.5 hover:bg-secondary/60"
                  >
                    <span className="block font-medium">{h.title}</span>
                    <span className="block text-sm text-muted-foreground">
                      {h.reference} · {h.customerName}
                      {h.doneBy ? ` · ${h.doneBy}` : ''}
                    </span>
                    {h.note && (
                      <span className="mt-1 block rounded-md bg-muted px-2 py-1 text-sm">
                        {h.note}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </Group>
          )}
          {d.failedEmails && d.failedEmails.length > 0 && (
            <Group
              icon={<MailWarning />}
              title="Emails that could not be sent"
              hint="Usually a mistyped address or a mail server problem. Fix the address, then try again."
            >
              {d.failedEmails.map((m) => (
                <li key={m.id} className="flex flex-wrap items-start gap-3 py-2.5">
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium break-words">{m.subject}</span>
                    <span className="block text-sm break-all text-muted-foreground">
                      To {m.to} · {ukDate(m.at, true)}
                    </span>
                    {m.error && (
                      <span className="mt-1 block rounded-md bg-muted px-2 py-1 text-sm">
                        {m.error}
                      </span>
                    )}
                  </span>
                  <span className="flex gap-2">
                    {m.quoteId && (
                      <Button
                        variant="ghost"
                        className="h-9"
                        render={<Link to="/quotes/$id" params={{ id: m.quoteId }} />}
                      >
                        Open quote
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="h-9"
                      onClick={() => retry.mutate(m.id)}
                      disabled={retry.isPending}
                    >
                      {retry.isPending && retry.variables === m.id ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        <RotateCcw />
                      )}
                      Try again
                    </Button>
                  </span>
                </li>
              ))}
            </Group>
          )}
        </div>
      ) : null}
    </>
  )
}

function Group({
  icon,
  title,
  hint,
  children,
}: {
  icon: ReactNode
  title: string
  hint: string
  children: ReactNode
}) {
  return (
    <section className="rounded-xl border bg-card">
      <header className="border-b px-5 py-3.5">
        <h2 className="flex items-center gap-2 text-[15px] font-semibold">
          <span className="text-primary [&_svg]:size-4" aria-hidden>
            {icon}
          </span>
          {title}
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">{hint}</p>
      </header>
      <ul className="divide-y px-5 py-1">{children}</ul>
    </section>
  )
}

function QuoteLine({ q, meta }: { q: QuoteItem; meta: ReactNode }) {
  return (
    <li>
      <Link
        to="/quotes/$id"
        params={{ id: q.id }}
        className="-mx-2 flex items-center gap-3 rounded-md px-2 py-2.5 hover:bg-secondary/60"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">
            {q.reference} · {q.customerName || q.title}
          </span>
          <span className="block text-sm text-muted-foreground">{meta}</span>
        </span>
        <StatusBadge status={q.status} />
      </Link>
    </li>
  )
}
